/**
 * Contact Validation & Normalization Utilities
 * Conforms to ITU-T E.164 and RFC 5322 validation standards.
 */

export interface ValidationResult {
  isValid: boolean
  error?: string
}

export interface PhoneValidationResult extends ValidationResult {
  normalized: string
}

/**
 * Strips whitespace, dashes, dots and parentheses.
 * Retains optional leading '+' followed strictly by digits.
 */
export function sanitizePhoneNumber(raw: string): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  const hasPlus = trimmed.startsWith('+')
  const digitsOnly = trimmed.replace(/\D/g, '')
  return hasPlus ? `+${digitsOnly}` : digitsOnly
}

/**
 * Validates WhatsApp / Phone numbers according to international formatting standards.
 * Digits length must be between 8 and 16 digits (including country code).
 */
export function validatePhoneNumber(phone: string): PhoneValidationResult {
  if (!phone || !phone.trim()) {
    return {
      isValid: false,
      normalized: '',
      error: 'El número de WhatsApp es obligatorio',
    }
  }

  const normalized = sanitizePhoneNumber(phone)
  const digitsCount = normalized.replace(/\D/g, '').length

  if (digitsCount < 8) {
    return {
      isValid: false,
      normalized,
      error: 'El número debe incluir código de país (mínimo 8 dígitos)',
    }
  }

  if (digitsCount > 16) {
    return {
      isValid: false,
      normalized,
      error: 'El número excede la longitud máxima internacional (16 dígitos)',
    }
  }

  return {
    isValid: true,
    normalized,
  }
}

/**
 * Validates an email address (optional, but must be valid if entered).
 */
export function validateEmailAddress(email: string): ValidationResult {
  const trimmed = email.trim()
  if (!trimmed) {
    return { isValid: true }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
  if (!emailRegex.test(trimmed)) {
    return {
      isValid: false,
      error: 'Ingresa un formato de email válido (ej: contacto@empresa.com)',
    }
  }

  return { isValid: true }
}
